import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';

import { toFormattedLocalDate } from 'utils/dateTime/converter';
import { DueDate } from 'components/common/DueDate/DueDate';
import { SHOW_ASSESSMENT_GUIDED_TOUR_KEY } from 'components/threepp/assessment/AssessmentGuidedTour/AssessmentGuidedTour';
import Button from 'components/common/Buttons/Button';
import { useViewport } from 'utils/getViewport/getViewport';
import SimpleAccordion from 'components/common/Accordion/SimpleAccordion';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';
import { getTimeRemaining } from 'utils/dateTime/DueDateCountDown';
import { PanelHeader } from 'components/common/Panel/Header';
import { stripHTML } from 'utils/functions';
import { SegmentedCountdown } from '../../ProgressBar/SegmentedCountdown';
import { OverviewDetails } from './OverviewDetails';
import { CircularProgressBar } from '../../ProgressBar/CircularProgressBar';
import './Overview.scss';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: '4rem'
  },
  stickySidebar: {
    [theme.breakpoints.down(theme.breakpoints.values.mobile)]: {
      height: 'calc(100  * var(--vh) - 44px)',
      overflowY: 'scroll'
    }
  },
  MuiButtonText: {
    justifyContent: 'end',
    textTransform: 'capitalize'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600
  },
  informationSectionContentWeb: {
    height: 'calc(100vh-155px)'
  },
  informationSectionContentMobile: {
    maxHeight: '100vh'
  },
  infoContent: {
    marginTop: '-.5rem'
  },
  details: {
    marginTop: '-.5rem'
  }
}));

export const Overview = ({
  assessment,
  assessmentHelpText,
  assessmentProgress,
  assessmentAnsweredQuestions,
  assessmentTotalQuestions,
  open,
  setTriggerGuidedTour
}) => {
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const owners = [];
  const classes = useStyles();

  assessment &&
    assessment.owners.map(assessmentOwner => {
      const { firstName, lastName } = assessmentOwner;
      owners.push(`${firstName} ${lastName}`);
      return owners;
    });
  const dueDate = assessment ? assessment.dueDate : new Date();

  const showOwners = isOpen ? owners : owners.slice(0, 5);

  const instructions = (stripHTML(assessmentHelpText).length > 0 ? assessmentHelpText : '') && (
    <OverviewDetails assessmentHelpText={assessmentHelpText} />
  );

  const onResetGuidedTour = () => {
    if (open) {
      EventEmitter.dispatch(EventEmitterTypes.TOGGLE_MOBILE_SIDEBAR, { right: 'right', open: false });
    }
    localStorage.setItem(SHOW_ASSESSMENT_GUIDED_TOUR_KEY, true);
    setTriggerGuidedTour();
  };
  const { days } = getTimeRemaining(dueDate);

  const overview = (
    <div>
      <CircularProgressBar assessmentProgress={assessmentProgress} />
      <h4 className="sidebar-progress">{`${assessmentAnsweredQuestions}/${assessmentTotalQuestions} ${t('common.complete')}`}</h4>
    </div>
  );

  const dueDateContent = assessment && (
    <div className={`${classes.infoContent} info-content due-date`}>
      <DueDate t={t} dueDate={assessment.dueDate} />
      <SegmentedCountdown days={days} />
    </div>
  );

  const details = assessment && (
    <div className={classes.details}>
      <div className="details-owner">
        <h6 className="details-label">{t('assessment.overview.ownerTitle')}</h6>
        <h6 className="details-value">
          {showOwners.map(owner => (
            <p className="owner-values" key={owner}>
              {owner}
            </p>
          ))}
        </h6>
        {owners.length > 5 && (
          <Button className="show-more-less" buttonStyle="text" onClick={toggle}>
            {isOpen ? t('assessment.overviewDetails.showLess') : t('assessment.overviewDetails.showMore')}
          </Button>
        )}
      </div>
      <div>
        <h6 className="details-label">{t('assessment.overview.dateCreated')}</h6>
        <h6 className="details-value">{toFormattedLocalDate(assessment.lastUpdated)}</h6>
      </div>
      <Button
        className={`btn-guided-tour ${classes.MuiButtonText}`}
        buttonStyle="text"
        size="small"
        onClick={onResetGuidedTour}
        textTransform="capitalize"
      >
        {t('help.guidedTour')}
      </Button>
    </div>
  );

  return (
    <section className={!isMobile ? `sidebar-none col-right ${classes.root}` : ''}>
      <div className={`${classes.stickySidebar} sticky-sidebar`}>
        <div className="information-section">
          <div className="guided-tour-right-panel-wrapper">
            <div className="guided-tour-right-panel" />
          </div>
          <PanelHeader title={t('assessment.overview.overviewTitle')} />
          <div className={!isMobile ? `${classes.informationSectionContentWeb}` : `${classes.informationSectionContentMobile}`}>
            {instructions && (
              <SimpleAccordion
                defaultExpanded={false}
                index={1}
                panelTitle={t('assessment.overview.instructions')}
                panelDetails={instructions}
                id="instructions"
              />
            )}
            <SimpleAccordion
              defaultExpanded
              index={2}
              panelTitle={t('assessment.overview.overallProgress')}
              panelDetails={overview}
              id="overallProgress"
            />
            <SimpleAccordion
              defaultExpanded
              index={3}
              panelTitle={`${t('assessment.dueDate.due')} ${dueDate ? toFormattedLocalDate(dueDate) : t('common.noValue')}`}
              panelDetails={dueDateContent}
              id="dueDate"
            />
            <SimpleAccordion defaultExpanded index={4} panelTitle={t('assessment.overview.details')} panelDetails={details} id="details" />
          </div>
        </div>
      </div>
    </section>
  );
};
